import { useState, useEffect, useRef } from 'react';
import API from '@/lib/api';
import IOSToggle from '@/components/IOSToggle';
import { Receipt, CheckCircle, ArrowCounterClockwise, Image as ImageIcon, Trash, UploadSimple } from '@phosphor-icons/react';

const PLACEHOLDERS = [
  { key: '{bedrijf}', omschr: 'Naam van uw onderneming' },
  { key: '{btw}', omschr: 'BTW-nummer' },
  { key: '{adres}', omschr: 'Volledig adres' },
  { key: '{tel}', omschr: 'Telefoonnummer' },
  { key: '{email}', omschr: 'E-mailadres' },
  { key: '{iban}', omschr: 'Bankrekening' },
  { key: '{datum}', omschr: 'Datum van ticket' },
  { key: '{tijd}', omschr: 'Tijdstip' },
  { key: '{ticket}', omschr: 'Ticketnummer' },
];

const STD_VOORWAARDEN = `Omruiling mogelijk binnen 14 dagen op vertoon van dit ticket. Geen terugbetaling op afgeprijsde artikelen. Garantie volgens de wettelijke bepalingen.`;

export default function KassaTicketPanel() {
  const [form, setForm] = useState({
    kassa_ticket_prefix: 'K',
    kassa_ticket_padding: 4,
    kassa_ticket_per_dag: true,
    kassa_ticket_header: '',
    kassa_ticket_voetekst: '',
    kassa_ticket_voorwaarden: STD_VOORWAARDEN,
    kassa_ticket_toon_btw: true,
    kassa_ticket_toon_bedrijfsinfo: true,
    kassa_ticket_toon_qr: false,
    kassa_ticket_toon_logo: true,
    kassa_ticket_toon_fotos: false,
    kassa_ticket_logo: '',
    kassa_ticket_logo_breedte: 'medium', // small | medium | large
  });
  const [bedrijfInst, setBedrijfInst] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const logoRef = useRef(null);

  const update = (k) => (v) => setForm(s => ({ ...s, [k]: typeof v === 'object' && v?.target ? v.target.value : v }));

  useEffect(() => {
    API.get('/instellingen').then(r => {
      const d = r.data || {};
      setBedrijfInst({
        naam: d.naam || '', btw: d.btw || '', adres: d.adres || '',
        tel: d.tel || '', email: d.email || '', iban: d.iban || '',
        logo: d.logo || '',
      });
      setForm(s => ({
        ...s,
        kassa_ticket_prefix: d.kassa_ticket_prefix || 'K',
        kassa_ticket_padding: d.kassa_ticket_padding || 4,
        kassa_ticket_per_dag: d.kassa_ticket_per_dag ?? true,
        kassa_ticket_header: d.kassa_ticket_header || '',
        kassa_ticket_voetekst: d.kassa_ticket_voetekst || '',
        kassa_ticket_voorwaarden: d.kassa_ticket_voorwaarden ?? STD_VOORWAARDEN,
        kassa_ticket_toon_btw: d.kassa_ticket_toon_btw ?? true,
        kassa_ticket_toon_bedrijfsinfo: d.kassa_ticket_toon_bedrijfsinfo ?? true,
        kassa_ticket_toon_qr: d.kassa_ticket_toon_qr ?? false,
        kassa_ticket_toon_logo: d.kassa_ticket_toon_logo ?? true,
        kassa_ticket_toon_fotos: !!d.kassa_ticket_toon_fotos,
        kassa_ticket_logo: d.kassa_ticket_logo || '',
        kassa_ticket_logo_breedte: d.kassa_ticket_logo_breedte || 'medium',
      }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await API.put('/instellingen', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  const resetVoorwaarden = () => setForm(s => ({ ...s, kassa_ticket_voorwaarden: STD_VOORWAARDEN }));

  const uploadLogo = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { alert('Alleen afbeeldingen toegestaan'); return; }
    if (f.size > 1_000_000) { alert('Logo te groot (max 1 MB)'); return; }
    const r = new FileReader();
    r.onload = () => setForm(s => ({ ...s, kassa_ticket_logo: r.result }));
    r.readAsDataURL(f);
  };

  const LOGO_BREEDTES = { small: 80, medium: 140, large: 200 };
  const effectLogo = form.kassa_ticket_logo || bedrijfInst.logo || '';
  const logoBreedtePx = LOGO_BREEDTES[form.kassa_ticket_logo_breedte] || 140;

  // Preview: vervang placeholders met live data
  const placeholderValues = {
    '{bedrijf}': bedrijfInst.naam || 'Uw Bedrijf',
    '{btw}': bedrijfInst.btw || 'BE0123.456.789',
    '{adres}': bedrijfInst.adres || 'Straat 1, 2000 Antwerpen',
    '{tel}': bedrijfInst.tel || '+32 3 123 45 67',
    '{email}': bedrijfInst.email || 'info@bedrijf.be',
    '{iban}': bedrijfInst.iban || 'BE68 5390 0754 7034',
    '{datum}': new Date().toLocaleDateString('nl-BE'),
    '{tijd}': new Date().toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' }),
    '{ticket}': `${form.kassa_ticket_prefix || 'K'}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(42).padStart(form.kassa_ticket_padding || 4, '0')}`,
  };
  const applyPlaceholders = (t) => {
    let out = t || '';
    Object.entries(placeholderValues).forEach(([k, v]) => { out = out.split(k).join(v); });
    return out;
  };

  // Voorbeeld-ticketID
  const voorbeeldTicketId = form.kassa_ticket_per_dag
    ? `${form.kassa_ticket_prefix || 'K'}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(42).padStart(form.kassa_ticket_padding || 4, '0')}`
    : `${form.kassa_ticket_prefix || 'K'}-${String(1042).padStart(form.kassa_ticket_padding || 4, '0')}`;

  return (
    <div className="space-y-5" data-testid="kassa-ticket-panel">
      <div className="flex items-start gap-4 p-4 rounded-lg border border-amber-200" style={{ background: 'linear-gradient(90deg,#FFFBEB 0%,#FFF7ED 100%)' }}>
        <div className="p-2 rounded bg-white border border-amber-200"><Receipt size={28} className="text-amber-600" /></div>
        <div className="flex-1">
          <div className="font-bold text-amber-800 mb-1">Kassaticket</div>
          <div className="text-xs text-gray-700">Stel de nummering, de extra header-regels en de verkoopvoorwaarden onderaan in. Dit verschijnt op elke afgedrukte kassabon.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LINKS: velden */}
        <div className="space-y-5">
          {/* Nummering */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Ticket-nummering</span></div>
            <div className="section-body space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Prefix</label>
                  <input
                    className="field-input font-mono"
                    style={{ minHeight: 40 }}
                    value={form.kassa_ticket_prefix}
                    maxLength={8}
                    onChange={update('kassa_ticket_prefix')}
                    placeholder="K"
                    data-testid="kt-prefix"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Bv. K, TKT, of uw winkel-afkorting (max 8 tekens).</p>
                </div>
                <div>
                  <label className="form-label">Aantal cijfers</label>
                  <select
                    className="field-input"
                    style={{ minHeight: 40 }}
                    value={form.kassa_ticket_padding}
                    onChange={e => update('kassa_ticket_padding')(parseInt(e.target.value))}
                    data-testid="kt-padding"
                  >
                    {[3, 4, 5, 6].map(n => {
                      const max = '9'.repeat(n);
                      return <option key={n} value={n}>{n} cijfers (max {max})</option>;
                    })}
                  </select>
                </div>
              </div>
              <IOSToggle
                label="Teller reset elke dag (K-YYYYMMDD-0001)"
                checked={form.kassa_ticket_per_dag}
                onChange={update('kassa_ticket_per_dag')}
                testid="kt-per-dag"
              />
              {!form.kassa_ticket_per_dag && <div className="text-[11px] text-amber-700 pl-7">Doorlopende nummering zonder datum (K-0001, K-0002, ...).</div>}
              <div className="mt-2 p-3 rounded bg-gray-50 border border-gray-200">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Voorbeeld-ID volgende ticket</div>
                <div className="text-xl font-mono font-bold text-gray-900 mt-1" data-testid="kt-voorbeeld">{voorbeeldTicketId}</div>
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Logo bovenaan het ticket</span>
              {effectLogo && !form.kassa_ticket_logo && <span className="text-[10px] text-gray-400">gebruikt bedrijfslogo</span>}
            </div>
            <div className="section-body space-y-3">
              <IOSToggle
                label="Logo afdrukken op de kassabon"
                checked={form.kassa_ticket_toon_logo}
                onChange={update('kassa_ticket_toon_logo')}
                testid="kt-toon-logo"
              />

              {form.kassa_ticket_toon_logo && (
                <>
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-28 h-24 rounded border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                      {effectLogo ? (
                        <img src={effectLogo} alt="Logo" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <ImageIcon size={24} className="text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        ref={logoRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={e => { uploadLogo(e.target.files); e.target.value = ''; }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary w-full justify-center"
                        onClick={() => logoRef.current?.click()}
                        data-testid="kt-logo-upload"
                      ><UploadSimple size={14} /> {form.kassa_ticket_logo ? 'Logo vervangen' : 'Kies logo'}</button>
                      {form.kassa_ticket_logo && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost w-full justify-center text-red-600"
                          onClick={() => update('kassa_ticket_logo')('')}
                          data-testid="kt-logo-del"
                        ><Trash size={14} /> Eigen logo verwijderen</button>
                      )}
                      <p className="text-[10px] text-gray-400">PNG / JPG / SVG / WEBP, max 1 MB. Zonder eigen logo gebruikt de bon automatisch uw bedrijfslogo uit Instellingen → Bedrijf → Algemeen.</p>
                    </div>
                  </div>

                  <div>
                    <label className="form-label mb-2">Logo-breedte op ticket</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: 'small', label: 'Klein', px: 80 },
                        { key: 'medium', label: 'Medium', px: 140 },
                        { key: 'large', label: 'Groot', px: 200 },
                      ].map(b => (
                        <button
                          type="button"
                          key={b.key}
                          onClick={() => update('kassa_ticket_logo_breedte')(b.key)}
                          className={`p-2 rounded border-2 transition text-center ${form.kassa_ticket_logo_breedte === b.key ? 'border-[#002FA7] bg-[#F0F4FF]' : 'border-gray-200 bg-white hover:border-gray-400'}`}
                          data-testid={`kt-logo-breedte-${b.key}`}
                        >
                          <div className="text-xs font-bold">{b.label}</div>
                          <div className="text-[10px] text-gray-500">{b.px}px</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Weergave-opties */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Ticket-inhoud</span></div>
            <div className="section-body space-y-3">
              <IOSToggle
                label="Bedrijfsgegevens tonen bovenaan (naam, adres, BTW)"
                checked={form.kassa_ticket_toon_bedrijfsinfo}
                onChange={update('kassa_ticket_toon_bedrijfsinfo')}
                testid="kt-toon-bedrijf"
              />
              <IOSToggle
                label="BTW-opsplitsing per tarief tonen"
                checked={form.kassa_ticket_toon_btw}
                onChange={update('kassa_ticket_toon_btw')}
                testid="kt-toon-btw"
              />
              <IOSToggle
                label="Ticket-QR-code tonen (voor terugname/controle)"
                checked={form.kassa_ticket_toon_qr}
                onChange={update('kassa_ticket_toon_qr')}
                testid="kt-toon-qr"
              />
              <IOSToggle
                label="🖼️ Artikel-foto's tonen naast elke lijn (30×30 px)"
                checked={form.kassa_ticket_toon_fotos}
                onChange={update('kassa_ticket_toon_fotos')}
                testid="kt-toon-fotos"
              />
            </div>
          </div>

          {/* Header */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Extra header-regel (optioneel)</span></div>
            <div className="section-body">
              <textarea
                className="field-input"
                rows={2}
                value={form.kassa_ticket_header}
                onChange={update('kassa_ticket_header')}
                placeholder="Bv. &quot;Bedankt voor uw bezoek!&quot; of &quot;Ma-Vr 9-18u · Za 10-17u&quot;"
                data-testid="kt-header"
                style={{ fontFamily: 'ui-monospace,monospace', fontSize: 12 }}
              />
              <p className="text-[10px] text-gray-400 mt-1">Verschijnt boven de lijnen. Leeg laten = geen header.</p>
            </div>
          </div>

          {/* Voetekst + Verkoopvoorwaarden */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Voetekst & verkoopvoorwaarden</span></div>
            <div className="section-body space-y-3">
              <div>
                <label className="form-label">Korte voetekst (boven de voorwaarden)</label>
                <input
                  className="field-input"
                  value={form.kassa_ticket_voetekst}
                  onChange={update('kassa_ticket_voetekst')}
                  placeholder="Bv. &quot;Bedankt en tot ziens!&quot; of &quot;Volg ons op Instagram @winkel&quot;"
                  data-testid="kt-voetekst"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="form-label mb-0">Verkoopvoorwaarden (onderaan ticket)</label>
                  <button type="button" className="text-[10px] text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" onClick={resetVoorwaarden} data-testid="kt-reset-voorwaarden"><ArrowCounterClockwise size={10} /> Standaard herstellen</button>
                </div>
                <textarea
                  className="field-input"
                  rows={5}
                  value={form.kassa_ticket_voorwaarden}
                  onChange={update('kassa_ticket_voorwaarden')}
                  placeholder="Bv. Omruiling binnen 14 dagen mits vertoon van dit ticket..."
                  data-testid="kt-voorwaarden"
                  style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11, lineHeight: 1.5 }}
                />
                <p className="text-[10px] text-gray-400 mt-1">Laat leeg om niks af te drukken. Gebruik placeholders zoals {'{bedrijf}'}, {'{btw}'}, {'{datum}'} hieronder.</p>
              </div>

              <div className="p-3 rounded bg-blue-50 border border-blue-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700 mb-1.5">Beschikbare placeholders</div>
                <div className="grid grid-cols-2 gap-1 text-[11px]">
                  {PLACEHOLDERS.map(p => (
                    <div key={p.key} className="flex items-center gap-1">
                      <code className="font-mono text-blue-700 bg-white px-1 rounded border border-blue-200">{p.key}</code>
                      <span className="text-gray-600 truncate">{p.omschr}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RECHTS: live preview van de bon */}
        <div className="lg:sticky lg:top-4 self-start">
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Live voorbeeld</span>
              <span className="text-[10px] text-gray-400">Kassa-bon simulatie</span>
            </div>
            <div className="section-body">
              <div
                className="rounded bg-white border border-dashed border-gray-300 mx-auto"
                style={{ maxWidth: 320, padding: 16, fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize: 11, lineHeight: 1.45, color: '#000' }}
                data-testid="kt-preview"
              >
                {form.kassa_ticket_toon_logo && effectLogo && (
                  <div className="text-center" style={{ marginBottom: 6 }}>
                    <img src={effectLogo} alt="Logo" style={{ maxWidth: logoBreedtePx, maxHeight: 80, display: 'inline-block', objectFit: 'contain' }} />
                  </div>
                )}
                {form.kassa_ticket_toon_bedrijfsinfo && (
                  <div className="text-center">
                    <div style={{ fontSize: 14, fontWeight: 'bold' }}>{bedrijfInst.naam || 'Uw Bedrijf'}</div>
                    {bedrijfInst.adres && <div style={{ fontSize: 10 }}>{bedrijfInst.adres}</div>}
                    {bedrijfInst.btw && <div style={{ fontSize: 10 }}>BTW {bedrijfInst.btw}</div>}
                    {(bedrijfInst.tel || bedrijfInst.email) && (
                      <div style={{ fontSize: 10 }}>
                        {bedrijfInst.tel}{bedrijfInst.tel && bedrijfInst.email ? ' · ' : ''}{bedrijfInst.email}
                      </div>
                    )}
                  </div>
                )}
                {form.kassa_ticket_header && (
                  <div className="text-center" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{applyPlaceholders(form.kassa_ticket_header)}</div>
                )}
                <div style={{ borderTop: '1px dashed #777', margin: '8px 0' }} />
                <div className="text-center" style={{ fontSize: 10 }}>
                  Ticket <b>{voorbeeldTicketId}</b><br />
                  {placeholderValues['{datum}']} · {placeholderValues['{tijd}']}
                </div>
                <div style={{ borderTop: '1px dashed #777', margin: '8px 0' }} />
                <table style={{ width: '100%', fontSize: 11 }}>
                  <tbody>
                    <tr><td>2× Brood multi</td><td style={{ textAlign: 'right' }}>€ 5,00</td></tr>
                    <tr><td>1× Kaas gouda 500g</td><td style={{ textAlign: 'right' }}>€ 8,50</td></tr>
                    <tr><td>1× Wijn Bordeaux</td><td style={{ textAlign: 'right' }}>€ 21,00</td></tr>
                  </tbody>
                </table>
                <div style={{ borderTop: '1px dashed #777', margin: '8px 0' }} />
                <table style={{ width: '100%', fontSize: 13, fontWeight: 'bold' }}>
                  <tbody><tr><td>TOTAAL</td><td style={{ textAlign: 'right' }}>€ 34,50</td></tr></tbody>
                </table>
                {form.kassa_ticket_toon_btw && (
                  <>
                    <div style={{ borderTop: '1px dashed #777', margin: '8px 0' }} />
                    <table style={{ width: '100%', fontSize: 10 }}>
                      <thead>
                        <tr style={{ color: '#666' }}><td>BTW</td><td style={{ textAlign: 'right' }}>Excl.</td><td style={{ textAlign: 'right' }}>BTW</td><td style={{ textAlign: 'right' }}>Incl.</td></tr>
                      </thead>
                      <tbody>
                        <tr><td>6%</td><td style={{ textAlign: 'right' }}>€ 12,74</td><td style={{ textAlign: 'right' }}>€ 0,76</td><td style={{ textAlign: 'right' }}>€ 13,50</td></tr>
                        <tr><td>21%</td><td style={{ textAlign: 'right' }}>€ 17,36</td><td style={{ textAlign: 'right' }}>€ 3,64</td><td style={{ textAlign: 'right' }}>€ 21,00</td></tr>
                      </tbody>
                    </table>
                  </>
                )}
                <div style={{ borderTop: '1px dashed #777', margin: '8px 0' }} />
                <table style={{ width: '100%', fontSize: 10 }}>
                  <tbody>
                    <tr><td>Contant</td><td style={{ textAlign: 'right' }}>€ 15,00</td></tr>
                    <tr><td>Bancontact</td><td style={{ textAlign: 'right' }}>€ 19,50</td></tr>
                  </tbody>
                </table>
                {form.kassa_ticket_toon_qr && (
                  <div className="text-center" style={{ margin: '8px 0' }}>
                    <div style={{ width: 80, height: 80, background: '#eee', border: '1px solid #ccc', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#888' }}>QR</div>
                    <div style={{ fontSize: 9, color: '#666' }}>Scan voor ticket online</div>
                  </div>
                )}
                {form.kassa_ticket_voetekst && (
                  <>
                    <div style={{ borderTop: '1px dashed #777', margin: '8px 0' }} />
                    <div className="text-center" style={{ whiteSpace: 'pre-wrap' }}>{applyPlaceholders(form.kassa_ticket_voetekst)}</div>
                  </>
                )}
                {form.kassa_ticket_voorwaarden && (
                  <>
                    <div style={{ borderTop: '1px dashed #777', margin: '8px 0' }} />
                    <div style={{ fontSize: 9, color: '#555', whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
                      <div style={{ fontWeight: 'bold', textTransform: 'uppercase', marginBottom: 4, color: '#000' }}>Verkoopvoorwaarden</div>
                      {applyPlaceholders(form.kassa_ticket_voorwaarden)}
                    </div>
                  </>
                )}
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-2">Dit is een simulatie. De echte bon gebruikt uw papier-breedte.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <div className="text-xs text-gray-500">Wijzigingen gelden voor de volgende afgedrukte kassa-bon.</div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Opgeslagen</span>}
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={busy}
            data-testid="kt-save"
          >{busy ? 'Opslaan…' : 'Opslaan'}</button>
        </div>
      </div>
    </div>
  );
}
